import ChatButton from 'core/chatbot/components/atoms/ChatButton';
import useAnalytics from 'core/hooks/useAnalytics';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STREAM_STATUS, isStreamingStatus } from '../../constants/status';
import { useAIChatConversation } from '../../contexts/AIChatConversation/useAIChatConversation';
import useChatbotPracticeView from '../../hooks/useChatbotPracticeView';
import { createPracticeService } from '../../services/practiceService';

function PracticeExerciseControls({
  activityInstanceId,
  hasUserInteracted,
  isChatAvailable,
  onActivityComplete,
  onQueueIDontKnow,
  onQueueWrongAnswer,
  questionRef,
  activityExtraInfo,
  sessionData,
}) {
  const { t } = useTranslation(['chatbot']);
  const { addActivityResultToSession, updateKCMastery, incrementCompletedActivities } = useChatbotPracticeView();
  const { currentConversationId, streamStatus } = useAIChatConversation();
  const { trackEvent } = useAnalytics();

  const [hasSubmittedAnswer, setHasSubmittedAnswer] = useState(false);
  const [isShowingSolution, setIsShowingSolution] = useState(false);
  const [isCheckingAnswer, setIsCheckingAnswer] = useState(false);
  const [nextActivityData, setNextActivityData] = useState(null);
  const [awaitingChatResponse, setAwaitingChatResponse] = useState(false);
  const studentResponseRef = useRef(null);

  const practiceService = useMemo(function () {
    return createPracticeService();
  }, []);

  // Clear awaiting chat response flag when streaming completes or errors
  useEffect(() => {
    if (streamStatus === STREAM_STATUS.IDLE || streamStatus === STREAM_STATUS.ERROR) {
      setAwaitingChatResponse(false);
    }
  }, [streamStatus]);

  const handleSubmit = useCallback(
    async function () {
      const api = questionRef?.current;
      if (!api) return;

      setIsCheckingAnswer(true);
      setHasSubmittedAnswer(true);

      api.checkAnswers();
      const response = api.getResponse();
      studentResponseRef.current = response;
      const isCorrect = response.score === response.max_score;

      // Store activity result and update KC mastery
      addActivityResultToSession({ isCorrect, response, activityInstanceId });
      updateKCMastery(response);

      // Track analytics event for activity answered
      trackEvent('practice_activity_answered', {
        activity_id: activityInstanceId,
        subject: sessionData?.subject || null,
        kc_id: activityExtraInfo?.associatedKcReferenceId || null,
        phase: activityExtraInfo?.activityPhase || null,
        result: isCorrect ? 'correct' : 'incorrect',
        template: activityExtraInfo?.template || null,
        IA_used: currentConversationId ? 'yes' : 'no',
      });

      try {
        const saveResponse = await practiceService.saveUserResponse(activityInstanceId, response);
        setNextActivityData(saveResponse?.nextActivity || null);
      } catch (error) {
        // Silent error, UI continues
        console.error(error);
      } finally {
        setIsCheckingAnswer(false);
      }

      if (isChatAvailable && !isCorrect && onQueueWrongAnswer) {
        onQueueWrongAnswer(response);
        setAwaitingChatResponse(true);
      }
    },
    [
      activityInstanceId,
      activityExtraInfo,
      currentConversationId,
      isChatAvailable,
      onQueueWrongAnswer,
      practiceService,
      questionRef,
      addActivityResultToSession,
      sessionData,
      trackEvent,
      updateKCMastery,
    ]
  );

  const handleViewSolution = useCallback(
    function () {
      questionRef.current?.showSolutions();
      setIsShowingSolution(true);
    },
    [questionRef]
  );

  const handleViewMyAnswer = useCallback(
    function () {
      const api = questionRef.current;
      if (!api) return;
      setIsShowingSolution(false);
      api.reset();
      const value = studentResponseRef.current?.value;
      if (value !== undefined) {
        api.setResponse(value);
        api.checkAnswers();
      }
    },
    [questionRef]
  );

  const handleNext = useCallback(
    function () {
      if (onActivityComplete) onActivityComplete(nextActivityData);
      incrementCompletedActivities();
    },
    [onActivityComplete, nextActivityData, incrementCompletedActivities]
  );

  return (
    <div className="ai-practice-view__actions">
      <div>
        {!hasSubmittedAnswer && (
          <ChatButton
            variant="outlined"
            onClick={function () {
              trackEvent('practice_tutor_hint_generated', {
                activity_id: activityInstanceId,
                kc_id: activityExtraInfo?.associatedKcReferenceId || null,
                practice_session_id: sessionData?.id || null,
              });

              if (onQueueIDontKnow) onQueueIDontKnow();
            }}
            size="large"
            disabled={!isChatAvailable}
          >
            {t('practice_button_dont_know')}
          </ChatButton>
        )}
        {hasSubmittedAnswer && !isShowingSolution && (
          <ChatButton variant="outlined" size="large" onClick={handleViewSolution}>
            {t('practice_button_view_solution')}
          </ChatButton>
        )}
        {hasSubmittedAnswer && isShowingSolution && (
          <ChatButton variant="outlined" size="large" onClick={handleViewMyAnswer}>
            {t('practice_button_view_my_answer')}
          </ChatButton>
        )}
      </div>
      <div>
        {!hasSubmittedAnswer && (
          <ChatButton variant="contained" size="large" onClick={handleSubmit} disabled={!hasUserInteracted}>
            {t('practice_button_check')}
          </ChatButton>
        )}
        {hasSubmittedAnswer && (
          <ChatButton variant="contained" size="large" onClick={handleNext} disabled={isCheckingAnswer || isStreamingStatus(streamStatus) || awaitingChatResponse}>
            {t('practice_button_next')}
          </ChatButton>
        )}
      </div>
    </div>
  );
}

PracticeExerciseControls.propTypes = {
  activityInstanceId: PropTypes.string,
  hasUserInteracted: PropTypes.bool,
  isChatAvailable: PropTypes.bool,
  onActivityComplete: PropTypes.func,
  onQueueIDontKnow: PropTypes.func,
  onQueueWrongAnswer: PropTypes.func,
  questionRef: PropTypes.object.isRequired,
  activityExtraInfo: PropTypes.object,
  sessionData: PropTypes.object,
};

PracticeExerciseControls.defaultProps = {
  activityInstanceId: undefined,
  hasUserInteracted: false,
  isChatAvailable: false,
  onActivityComplete: undefined,
  onQueueIDontKnow: undefined,
  onQueueWrongAnswer: undefined,
  activityExtraInfo: undefined,
  sessionData: undefined,
};

export default PracticeExerciseControls;
